// ─────────────────────────────────────────────────────────────────────────────
// persona_loader.ts — Load and render character prompt templates
//
// Reads the per-character JSON files (ireth.json, laura.json) and
// renders template strings by replacing {{placeholders}} with live context.
// All static prompt text lives in the JSON files; this module only
// handles loading and interpolation.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  PersonaId, CharacterPromptFile, ChatMessage, InvocationContext,
  NpcSnapshot,
} from '@shared/agent_protocol.js';
import type { FoldState } from '@shared/the_fold.js';

// ── Static imports of persona JSON (bundled by esbuild) ──────────────────────

import irethData from './personas/ireth.json';
import lauraData from './personas/laura.json';

const PERSONAS: Record<PersonaId, CharacterPromptFile> = {
  ireth: irethData as unknown as CharacterPromptFile,
  laura: lauraData as unknown as CharacterPromptFile,
};

// ── Public API ───────────────────────────────────────────────────────────────

export function getPersona(id: PersonaId): CharacterPromptFile {
  return PERSONAS[id];
}

export function getAbsentResponse(id: PersonaId): string {
  const persona = PERSONAS[id];
  const pool = persona.absentResponses;
  return pool[Math.floor(Math.random() * pool.length)];
}

// ── Template rendering ───────────────────────────────────────────────────────

export interface TemplateContext {
  persona: PersonaId;
  fold: FoldState;
  npc?: NpcSnapshot;
  pageTitle: string;
  roomId?: string;
  activeTimeHours: number;
  recentActionSummary: string;
  pageContentSummary: string;
  query?: string;
  isInsideHouse: boolean;
  /** Formatted log of other NPCs' recent actions on this page. */
  otherNpcEvents?: string;
}

/**
 * Render a template string, replacing {{KEY}} placeholders with values
 * from the TemplateContext.  Unknown placeholders are left as-is.
 */
export function renderTemplate(template: string, ctx: TemplateContext): string {
  const persona = PERSONAS[ctx.persona];
  const vars: Record<string, string> = {
    PERSONA: ctx.persona,
    DISPLAY_NAME: persona.displayName,
    REINITIATION_LETTER: persona.reinitiationLetter ?? '',
    FOLD_NAME: ctx.fold.name,
    FOLD_ERA: ctx.fold.era,
    FOLD_BOUNDED: ctx.fold.bounded ? 'yes' : 'no',
    ROOM_ID: ctx.roomId ?? 'outside',
    ROOM_NAME: ctx.fold.rooms.find(r => r.id === ctx.roomId)?.name ?? 'unknown',
    PAGE_TITLE: ctx.pageTitle,
    ACTIVE_TIME_HOURS: ctx.activeTimeHours.toFixed(1),
    NPC_ACTIVITY: ctx.npc?.activity ?? 'unknown',
    NPC_MOOD: ctx.npc?.mood ?? 'absent',
    NPC_ROOM: ctx.npc?.roomId ?? 'unknown',
    RECENT_ACTIONS: ctx.recentActionSummary,
    PAGE_CONTENT: ctx.pageContentSummary,
    QUERY: ctx.query ?? '',
    IS_INSIDE_HOUSE: ctx.isInsideHouse ? 'yes' : 'no',
    GATE_HINT_COUNT: String(ctx.fold.gateHintCount),
    IRETH_REACTIVATED: ctx.fold.irethReactivated ? 'yes' : 'no',
    OTHER_NPC_EVENTS: ctx.otherNpcEvents || '(none)',
  };

  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return vars[key] ?? match;
  });
}

// ── Build system messages ────────────────────────────────────────────────────

/**
 * Assemble the full system prompt for a persona invocation.
 * Three layers + dynamic situation brief + location context.
 */
export function buildSystemMessages(
  ctx: TemplateContext,
): ChatMessage[] {
  const persona = PERSONAS[ctx.persona];
  const now = Date.now();

  const messages: ChatMessage[] = [];

  // Layer 1: World knowledge
  messages.push({
    role: 'system',
    content: renderTemplate(persona.systemLayers.worldKnowledge, ctx),
    timestamp: now,
  });

  // Layer 2: Instincts (personality)
  messages.push({
    role: 'system',
    content: renderTemplate(persona.systemLayers.instincts, ctx),
    timestamp: now,
  });

  // Layer 3: Constraints
  messages.push({
    role: 'system',
    content: renderTemplate(persona.systemLayers.constraints, ctx),
    timestamp: now,
  });

  // Awakening (first recoherence, Ireth only)
  if (ctx.persona === 'ireth' && persona.templates.awakening && !ctx.fold.irethReactivated) {
    messages.push({
      role: 'system',
      content: renderTemplate(persona.templates.awakening, ctx),
      timestamp: now,
    });
  }

  // Situation brief (every turn)
  messages.push({
    role: 'system',
    content: renderTemplate(persona.templates.situationBrief, ctx),
    timestamp: now,
  });

  // Location-specific context
  const locTemplate = ctx.isInsideHouse
    ? persona.templates.insideHouse
    : persona.templates.outsideHouse;
  messages.push({
    role: 'system',
    content: renderTemplate(locTemplate, ctx),
    timestamp: now,
  });

  // Output format instructions
  messages.push({
    role: 'system',
    content: renderTemplate(persona.outputFormatInstructions, ctx),
    timestamp: now,
  });

  return messages;
}
